<script lang="ts">
	import { setMetaPixelConsent } from "@utils/metaPixel.js";
	import { onMount } from "svelte";
	import { on } from "svelte/events";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";

	const { META_PIXEL } = getGlobalSettings();

	// The pixel is initialised with consent revoked, so PageView is only sent once the visitor
	// accepts targeting cookies. Guarded because both the initial state check and the accept
	// event can grant consent within a single page load.
	let pageViewSent = false;

	// Meta's base code, rendered into the head so that the `fbq` queue exists before hydration.
	// Svelte HTML-escapes the text content of an element, and a browser does not decode entities
	// back inside a <script>, so this snippet must stay free of `<` and `&`.
	const baseCode = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version="2.0";n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,"script","https://connect.facebook.net/en_US/fbevents.js");
fbq("consent", "revoke");
fbq("init", ${JSON.stringify(META_PIXEL)});`;

	function revokeConsent(): void {
		window.fbq?.("consent", "revoke");
		setMetaPixelConsent(false);
	}

	function applyConsent(categories: readonly CookieScriptCategory[]): void {
		if (!categories.includes("targeting")) {
			revokeConsent();
			return;
		}

		window.fbq?.("consent", "grant");
		if (!pageViewSent) {
			pageViewSent = true;
			window.fbq?.("track", "PageView");
		}
		setMetaPixelConsent(true);
	}

	function processState(): void {
		const state = CookieScript.instance.currentState();
		if (state.action === "accept") {
			applyConsent(state.categories ?? []);
		} else {
			revokeConsent();
		}
	}

	onMount(() => {
		if (!META_PIXEL) {
			return;
		}

		if (window.CookieScript?.instance) {
			processState();
		} else {
			on(window, "CookieScriptLoaded", processState, { once: true });
		}

		const offAccept = on(window, "CookieScriptAccept", (e) => {
			applyConsent(e.detail.categories ?? []);
		});
		const offReject = on(window, "CookieScriptReject", revokeConsent);

		return (): void => {
			offAccept();
			offReject();
		};
	});
</script>

<svelte:head>
	{#if META_PIXEL}
		<link rel="preconnect" href="https://connect.facebook.net" />
		<svelte:element this={"script"}>{baseCode}</svelte:element>
	{/if}
</svelte:head>
